import { Canvas, useFrame, useThree } from '@react-three/fiber'
// import { OrbitControls } from '@react-three/drei';
// import * as THREE from 'three';

import React from 'react';
import { useEffect, useRef } from 'react';

const Spheres = React.memo(({ amount, color }) => {
    const sphereRefs = useRef([]);
    const velocities = useRef([]);
    // Use target/current for mouse smoothing
    const targetMousePos = useRef({ x: 0, y: 0 });
    const currentMousePos = useRef({ x: 0, y: 0 });
    // Track if we should be repelling
    const interactionStrength = useRef(0);
    const mouseLeaveTimeout = useRef(null);

    // Generate initial positions once
    const initialPositions = React.useMemo(() => {
        const positions = [];
        // On smaller screens, push particles back (negative Z) to avoid being too close
        // If width is < 1000, we start adjusting. e.g. at 400px width, offset is -600
        const zShift = Math.max(0, 800 - window.innerWidth);
        
        for (let i = 0; i < amount; i++) {
            positions.push({
                x: (Math.random() * window.innerWidth - window.innerWidth * 0.5),
                y: (Math.random() * window.innerHeight - window.innerHeight * 0.5),
                z: (Math.random() * 300) - zShift
            });
        }
        return positions;
    }, [amount]);

    useEffect(() => {
        const onMouseMove = (e) => {
            // Cancel any pending leave timeout
            if (mouseLeaveTimeout.current) {
                clearTimeout(mouseLeaveTimeout.current);
                mouseLeaveTimeout.current = null;
            }

            // Target position from mouse
            targetMousePos.current.x = (e.clientX / window.innerWidth) * 2 - 1;
            targetMousePos.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
            
            // Set full interaction strength immediately on move
            interactionStrength.current = 1;
        };

        const onMouseLeave = () => {
            // Add a small delay before fading out to handle quick edge movements
            mouseLeaveTimeout.current = setTimeout(() => {
                interactionStrength.current = 0;
            }, 100);
        };

        const onClick = (e) => {
            // Shockwave effect
            const clickX = (e.clientX / window.innerWidth) * 2 - 1;
            const clickY = -(e.clientY / window.innerHeight) * 2 + 1;
            
            const clickMouseX = clickX * window.innerWidth * 0.5;
            const clickMouseY = clickY * window.innerHeight * 0.5;

            const shockwaveRadius = 800;
            const shockwaveStrength = 20;

            sphereRefs.current.forEach((sphere, i) => {
                if (sphere) {
                    if (!velocities.current[i]) {
                        velocities.current[i] = { x: 0, y: 0, z: 0 };
                    }
                    const velocity = velocities.current[i];
                    
                    const dx = sphere.position.x - clickMouseX;
                    const dy = sphere.position.y - clickMouseY;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < shockwaveRadius) {
                         const force = (1 - distance / shockwaveRadius) * shockwaveStrength;
                         const angle = Math.atan2(dy, dx);
                         
                         velocity.x += Math.cos(angle) * force;
                         velocity.y += Math.sin(angle) * force;
                    }
                }
            })
        }

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseleave', onMouseLeave);
        window.addEventListener('mousedown', onClick);
        
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseleave', onMouseLeave);
            window.removeEventListener('mousedown', onClick);
            if (mouseLeaveTimeout.current) clearTimeout(mouseLeaveTimeout.current);
        };
    }, []);

    useFrame((state, delta) => {
        // Smoothly interpolate current mouse pos towards target
        currentMousePos.current.x += (targetMousePos.current.x - currentMousePos.current.x) * 0.1;
        currentMousePos.current.y += (targetMousePos.current.y - currentMousePos.current.y) * 0.1;
        
        // Also smoothly fade the interaction strength if it was set to 0
        // We handle simple lerp manually here for the strength multiplier
        // However, we just set it to 0 or 1 in events, so we can lerp it here for smooth fade out
        // Let's rely on the physics damping for the "drift stop" but we can also damp the force multiplier
        
        sphereRefs.current.forEach((sphere, i) => {
            if (sphere) {
                // Initialize velocity
                if (!velocities.current[i]) {
                    velocities.current[i] = { x: 0, y: 0, z: 0 };
                }
                const velocity = velocities.current[i];

                // Get mouse position in 3D world space using SMOOTHED position
                const mouseX = currentMousePos.current.x * window.innerWidth * 0.5;
                const mouseY = currentMousePos.current.y * window.innerHeight * 0.5;
                
                // Calculate distance from particle to mouse
                const dx = sphere.position.x - mouseX;
                const dy = sphere.position.y - mouseY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                // Repulsion settings
                const repulsionRadius = 200; 
                const maxRepulsionStrength = 1; // Physics based acceleration
                
                // Effective strength relies on interaction state (fade out on leave)
                // We could also lerp this value frame-by-frame for smoother transition if we stored it as a ref distinct from target
                const currentStrength = maxRepulsionStrength * interactionStrength.current;

                if (distance < repulsionRadius && interactionStrength.current > 0.01) {
                    const force = (1 - distance / repulsionRadius) * currentStrength;
                    const angle = Math.atan2(dy, dx);
                    
                    velocity.x += Math.cos(angle) * force;
                    velocity.y += Math.sin(angle) * force;
                }
                
                // Damping / Friction (Inertia)
                velocity.x *= 0.92; // Higher = more slippery/slidey
                velocity.y *= 0.92;
                velocity.z *= 0.92;

                // Return to origin (Spring force)
                const originalPos = initialPositions[i];
                const springStrength = 0.01; // Strength of the pull back
                
                velocity.x += (originalPos.x - sphere.position.x) * springStrength;
                velocity.y += (originalPos.y - sphere.position.y) * springStrength;
                // velocity.z += (originalPos.z - sphere.position.z) * springStrength; // Optional z-axis return
                
                // Original drift (ambient motion)
                // We keep this to give it life even when returning
                const driftSpeed = 0.05;
                const randomFactor = Math.random() - 0.5;
                
                // Apply velocity to position
                sphere.position.x += velocity.x + (randomFactor * driftSpeed * delta * 0.2);
                sphere.position.y += velocity.y + (randomFactor * driftSpeed * delta * 0.2);
                sphere.position.z += velocity.z + (randomFactor * driftSpeed * delta * 0.2);
            }
        });
    });

    return (
        <>
            {initialPositions.map((pos, i) => (
                <mesh
                    key={i}
                    ref={(el) => (sphereRefs.current[i] = el)}
                    position={[pos.x, pos.y, pos.z]}
                >
                    <sphereGeometry args={[0.5, 6, 6]} />
                    <meshStandardMaterial color={color} />
                </mesh>
            ))}
        </>
    );
});

const MouseControlledCamera = () => {
    const { camera } = useThree();
    const mouseTolerance = 0.01;
    const smoothing = 1;

    useEffect(() => {
        let targetX = 0;
        let targetY = 0;

        const onMouseMove = (e) => {
            const centerX = window.innerWidth * 0.5;
            const centerY = window.innerHeight * 0.5;
            targetX = -(e.clientX - centerX) * mouseTolerance;
            targetY = (e.clientY - centerY) * mouseTolerance;
            camera.position.x += (targetX - camera.position.x) * smoothing;
            camera.position.y += (targetY - camera.position.y) * smoothing;
        };

        window.addEventListener("mousemove", onMouseMove);

        return () => {
            window.removeEventListener("mousemove", onMouseMove);
        };
    }, [camera, mouseTolerance]);

    return null;
};

const CameraController = ({ newposition, hypo }) => {
    useFrame((state) => {
        const { camera } = state;
        const targetPosition = camera.position.clone();
        targetPosition.z = Number(newposition*10) + hypo;
        camera.position.lerp(targetPosition, 0.03);
    });
    
    return null;
};

export default function Particles({color, newposition}) {
    const hypo = Math.sqrt(window.innerWidth ** 2 - (window.innerWidth / 2) ** 2) / 2;

    const cameraSettings = {
        fov: 90, 
        near: 0.1, 
        far: hypo+500, 
        position: [0, 0, hypo]
    }

    return (
        <div className='' style={{ width: '100vw', height: '100vh' }}>
            <Canvas camera={cameraSettings}>
                <CameraController newposition={newposition} hypo={hypo} />
                <MouseControlledCamera />
                <Spheres amount={(window.innerWidth*window.innerHeight)/1000} color={color}/>
                <ambientLight intensity={1} />
            </Canvas>
        </div>
    )
}

